import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import type { NodeProps, Node } from '@xyflow/react';
import {
  PlusIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import type { ImplementationPlanColumn } from '@mm-shared/types/Objective1';
import { BadgeButton } from '../../components_catalyst/badge';
import { Checkbox } from '../../components_catalyst/checkbox';
import { Select } from '../../components_catalyst/select';
import { Dialog, DialogTitle, DialogBody, DialogActions } from '../../components_catalyst/dialog';
import { Textarea } from '../../components_catalyst/textarea';
import { Button } from '../../components_catalyst/button';
import { CollapseIcon } from '../../components_misc/CollapseIcon';
import { ExpandIcon } from '../../components_misc/ExpandIcon';
import {
  PLAN_COLUMNS,
  COLLAPSED_BLOCK_HEIGHT,
  EYEBROW_BLOCK_HEIGHT,
  TITLE_BLOCK_HEIGHT,
  INFO_BLOCK_HEIGHT,
  INFO_BLOCK_GAP,
  KEY_RESULT_LIST_MARGIN_TOP,
  KEY_RESULT_ROW_BOX_HEIGHT,
  FOOTER_HEIGHT,
  FOOTER_SELECT_FONT_SIZE,
  FOOTER_SELECT_PAD_X_END,
  FOOTER_SELECT_PAD_X_START,
  FOOTER_SELECT_PAD_Y,
  FOOTER_SELECT_WIDTH,
  cardHasAdditionalInfo,
  keyResultsListHeight,
} from '../utils/planLayout';
import { cardStatusColorClass, cardStatusLabel } from '../utils/cardStatus';
import type { StickyNoteNodeData } from '../types';

/**
 * ObjectiveStickyNoteNode — one objective as a sticky note (ADR §2, §5, A1).
 *
 * Interactions (canEdit only):
 * - Click the title to edit it inline; Enter/blur with non-empty trimmed text
 *   submits, empty submit or Escape cancels with no write.
 * - "+" in the footer opens an inline key-result input with the same
 *   submit/cancel contract — createResult1 throws on empty text, so a result
 *   is never created on click alone (ADR §5).
 * - Click a key-result row to edit its **text** in place, with the identical
 *   Enter/blur/Escape contract (ADR A3a/A3b). Text is the only editable field
 *   on a row: no deadline, no status, no delete. One row at a time
 *   (`editingResultUuid`), and the move controls hide while any inline edit is
 *   open — a footer control is a live pointer target during an edit, so one
 *   click would blur-commit the edit *and* dispatch a move (A3b).
 * - Toggle a key result's checkbox to flip `delivered` (ADR.ImplementationPlansUX2
 *   C2a/C2b) — its own control, independent of the row's text editor, and
 *   present in all three row renders (editing input, editable button,
 *   read-only div) so the row's control count never depends on role or edit
 *   state (A1d). `!canEdit` renders it `disabled` with `aria-checked` intact
 *   rather than hiding it (C2c) — a viewer can read delivered state.
 *
 * Every interactive child carries the `nodrag` class (ReactFlow's
 * noDragClassName default) on itself or on an ancestor inside the card — which
 * is what ReactFlow actually requires, since its `hasSelector` walks from the
 * event target up to the node root. Catalyst's `Select` takes the class on its
 * wrapper `<span>` (`className` never reaches the native control); everything
 * else carries it directly. Pointerdown on any of them edits instead of
 * starting a node drag; the card's remaining surface stays the drag surface
 * (ADR §2 "Click-vs-drag disambiguation", INV-9).
 *
 * **Geometry (ADR A1-R1, INV-6).** Dimensions come from `planLayout` via node
 * data — never measured. Every vertical block renders with an inline `height`
 * read from a `planLayout` constant, an inline `flexShrink: 0`, and a
 * `data-block` name. `flexShrink: 0` is load-bearing, not belt-and-braces: the
 * card root is a column flex container with a definite height, so a block's
 * pinned height is a *basis*, not a floor — without it an over-budget card
 * silently squashes its blocks instead of overflowing, and every guard passes.
 *
 * `overflow: hidden` is pinned inline **only on elements that own their text**
 * (A1-R1b, WCAG 2.4.7): an outline is painted outside the border box and is
 * clipped by an *ancestor's* overflow but never by the element's own, so a
 * clipping wrapper erases its interactive child's focus ring. Wrappers that host
 * a control budget `FOCUS_RING_CLEARANCE` instead (the eyebrow), or accept a few
 * pixels of overlap (the key-result rows). No edit affordance changes any pin:
 * card height is independent of edit state (A1e, INV-13).
 *
 * The key-result rows became interactive in mr-kv9w.16, which moved the clip
 * with the text: the `<li>` is now a pinned, **non-clipping** wrapper and
 * `truncate` + the `title` attribute live on the row's text-owning control. A
 * clipping wrapper around a control in a 20px box with zero clearance does not
 * trim that control's focus ring, it erases it (A1-R1b, WCAG 2.4.7) — the
 * classification is parent-aware, and this row is the case that proves it.
 *
 * The eyebrow is filled here (mr-kv9w.15): a copy-only `BadgeButton` for the
 * objective's `shortId`, reusing the Map3Drawer info-tab pattern verbatim
 * (ADR A2a) — same markup, same handler shape, same "Copied!" affordance. It
 * copies; it never navigates (ADR A2b), and it renders for every role, since
 * copying an id is not an edit. A legacy objective with no `shortId` still
 * renders the pinned band, just without the badge — the B4 pair still fills
 * it (A1b/A1d) — the block is never conditional. The geometry is final since
 * mr-kv9w.15.
 *
 * `additionalInfo` is filled here (mr-kv9w.17, ADR A4): the `info` block
 * renders a 2-line `line-clamp-2` preview with a `title` attribute carrying
 * the full text when non-empty, and no block at all when empty (A4a) — the
 * conditional term is `cardHasAdditionalInfo`, shared verbatim with
 * `computeLayout`'s `cardHeight` call so the budgeted block and the rendered
 * block can never disagree. A footer `DocumentTextIcon` trigger opens a
 * Catalyst `Dialog` + `Textarea` editor; it renders **for every role**
 * (A4b-i) — editors get a Save/Cancel editor, viewers get a `readOnly`
 * Textarea and a single Close button, so the full text has a keyboard-
 * reachable route regardless of role. The dialog is portaled by
 * `Headless.Dialog`, so it costs zero card height in any state (A1e) and
 * needs no `nodrag`: nothing inside it is a descendant of the ReactFlow node.
 * Focus restore to the trigger after close relies on Headless UI's default
 * (WCAG 2.4.3), pinned by an explicit test rather than assumed (INV-11
 * precedent).
 *
 * **The title-only render (ADR B5).** When `card.collapse === 'titleOnly'` the
 * card renders exactly ONE block — `data-block="collapsed"` — holding the
 * truncated title, and nothing else. This is a block-SET choice, not a
 * per-block one: a card renders `{eyebrow, title, info?, key-results?, footer}`
 * **or** `{collapsed}`, never a mixture, because a card rendering both would
 * hold the sum of two budgets while every per-block guard still passed (INV-6's
 * amended clause). The short-id copy, title editing, the key-result rows and
 * their editors, the details trigger and dialog and the move controls are all
 * simply **not rendered** while collapsed — not disabled, not `canEdit`-gated —
 * because the view is a title-only view; they all return on expand.
 *
 * The collapsed title is a plain, non-interactive `<div>`: it carries no
 * `nodrag`, so the whole row except the B4 view controls is drag surface
 * (B8), and with no editor in the collapsed render INV-13 is preserved
 * trivially rather than argued.
 *
 * **The card-view button pair (ADR B4/B4a/B11).** Two permanently-mounted,
 * never-disabled `nodrag` buttons — `CollapseIcon` then `ExpandIcon`,
 * `MapTitleBar`'s order — render in BOTH collapse states and for EVERY role:
 * collapsing is a view operation, not an edit (the same reasoning A2a used
 * for the copy affordance). They live at the right end of the `eyebrow`
 * block in the expanded render and at the right end of the `collapsed`
 * block in the title-only render — one wrapper, `role="group"
 * aria-label="Card view"`, so the mutually-exclusive pair reads as one
 * control to a screen reader rather than two unrelated toggles. Clicking the
 * button for the card's CURRENT state is a silent no-op at the canvas (B3's
 * sorted `collapseKey` does not move) — this component always calls
 * `onSetCollapse` with the explicit target state and lets the canvas decide.
 *
 * **The A3b hide gate applies to the EXPANDED render only** (B4a's Critical
 * fix): while an inline edit is open, the pair is hidden alongside the
 * footer's move controls and details trigger, because a live pointer target
 * next to an open editor turns one click into a blur-commit plus a second
 * action. The collapsed render never gates on `isEditing` — computed after
 * the collapsed branch's early return — so a collapsed card ALWAYS shows
 * both buttons, even mid-edit: entering `titleOnly` while a draft is open
 * cannot brick the card, and the `useEffect` below clears the stale draft on
 * the way in so re-expanding never restores a stale editor.
 *
 * **Status colour (ADR.ImplementationPlansUX2 C1d/C1f).** The card wears the
 * objective's latest calendar-timeline status colour, carried by TWO visible
 * channels plus the accessible name, because neither visible one survives
 * every theme on its own:
 *
 * 1. **A rail** — 8px x full card height of the grid cell's own
 *    `bg-status-*` class at **100% opacity**. Nothing is ever rendered over
 *    it, which is why it can be full strength where the card body cannot. It
 *    sits inside the card's existing `p-3` left padding (8 < 12), so it
 *    overlaps no content and pins no budget term (C1e). Its
 *    **content-facing hairline** (`border-r border-zinc-900`) is not trim: five
 *    `grayscale` tokens resolve to `#ffffff` exactly, so a full-opacity rail on
 *    a white card is **1.00:1 — mathematically invisible**. The hairline is
 *    what makes every card with a status structurally distinguishable from
 *    every card without one, by a bound that never reads the token's value
 *    (C1d-R2, INV-19). Under `border-box` (Tailwind preflight) `w-2 border-r`
 *    is 8px total, not 9.
 * 2. **A tint** at **45%** — gestalt, not fidelity. 45% is the ceiling the
 *    contrast bound allows and the floor that separates the light tokens.
 *    `-inset-px`, not `inset-0`: an absolutely-positioned box resolves against
 *    the containing block's *padding* box, so `inset-0` leaves an uncoloured
 *    ring visible through the translucent border. `isolate` on the root +
 *    `-z-10` on both layers keeps the negative indices inside the card.
 * 3. **The accessible name** — `Objective: … Status: …` on the node wrapper
 *    (`Map3ImplementationPlans`) and, for the pointer, the same string as the
 *    card root's `title`. There is deliberately **no visible status glyph**
 *    (bead mr-60dv.13, product decision): the board's 1.4.1 posture is the
 *    grid's own, where a status cell is colour-only by default and the codes
 *    appear only behind the grid's `showStatusCodes` toggle. The rail's
 *    *presence* is what remains as a non-hue visible cue, and the rail's
 *    absence is itself a cue — no status means no rail, no hairline, and the
 *    neutral **gray default** (mr-60dv.14).
 *
 * **The gray default (C1d, mr-60dv.14).** A card with no status colour is
 * `border-zinc-200 bg-zinc-100` — a theme-independent neutral, replacing the
 * shipped amber, which read as yellow and collided with a real `status-y`.
 * Yellow on this board now means exactly one thing: a yellow status. The gray
 * is a fixed Tailwind token, never a `bg-status-*` one, so no theme can move
 * it under the text (`text-zinc-900` on `zinc-100` is 16.1:1 in every theme)
 * and the "no card text over a full-strength status fill" rule (C1d) holds
 * unchanged. A card whose latest status IS `'p'` keeps the full status
 * treatment — gray rail, gray 45% tint — so the rail is what separates
 * *planning was recorded* from *nothing was recorded*.
 *
 * **One text colour (C1d-R1a).** Every card-owned text run and icon glyph is
 * `text-zinc-900`. The darkest composite reachable under card text is the 45%
 * tint over white with a `#000000` token (`L = 0.2633`, `L = 0.2354` under
 * `hover:bg-black/5`), against which `text-zinc-900` is 5.29:1 / 4.82:1 and
 * `text-zinc-700`, `text-zinc-600` and `text-green-600` all FAIL. A rule with
 * one member is also the only one a unit test can state exhaustively.
 * **Every element with its own background gets an opaque plate** (C1d-R1b),
 * because that arithmetic is only valid where the composite is "tint over
 * white": the shortId `BadgeButton` is wrapped in a `bg-white` span — a plate,
 * not a class override, since Catalyst renders the translucent `<Badge>`
 * *inside* the button `className` lands on — and the three inline editors are
 * already `bg-white` (their `border-amber-300` becomes `border-zinc-500`
 * because amber on a red card is a hue clash, not because the border carries
 * the boundary; the opaque fill does — and on the gray default it is the
 * `border-zinc-500` that carries it, at 4.40:1 against `zinc-100`, since
 * white-on-`zinc-100` is only 1.10:1).
 */

/**
 * The card-view pair's shared markup (ADR B4) — one function so the
 * expanded and collapsed renders cannot drift apart on an attribute. Icons
 * never swap: `CollapseIcon` always sits on the Collapse button, `ExpandIcon`
 * always on the Expand button; `aria-pressed` is what exposes state.
 */
function CardViewControls({
  card,
  objectiveLabel,
  onSetCollapse,
  className = '',
}: {
  card: StickyNoteNodeData['card'];
  objectiveLabel: string;
  onSetCollapse?: StickyNoteNodeData['onSetCollapse'];
  className?: string;
}) {
  return (
    <div role="group" aria-label="Card view" className={`flex items-center gap-1 ${className}`}>
      <button
        type="button"
        data-control="collapse-card"
        className="nodrag rounded p-0.5 text-zinc-900 hover:bg-black/5"
        aria-label={`Collapse card: ${objectiveLabel}`}
        aria-pressed={card.collapse === 'titleOnly'}
        title="Collapse to title only"
        onClick={() => onSetCollapse?.(card.uuid, 'titleOnly')}
      >
        <CollapseIcon className="h-4 w-4" aria-hidden="true" />
      </button>
      <button
        type="button"
        data-control="expand-card"
        className="nodrag rounded p-0.5 text-zinc-900 hover:bg-black/5"
        aria-label={`Expand card: ${objectiveLabel}`}
        aria-pressed={card.collapse === 'expanded'}
        title="Expand to full view"
        onClick={() => onSetCollapse?.(card.uuid, 'expanded')}
      >
        <ExpandIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

export function ObjectiveStickyNoteNode({ data }: NodeProps<Node<StickyNoteNodeData>>) {
  const {
    card,
    canEdit,
    onEditTitle,
    onAddKeyResult,
    onEditKeyResult,
    onToggleDelivered,
    onEditAdditionalInfo,
    onKeyboardMove,
    onSetCollapse,
  } = data;

  const [titleDraft, setTitleDraft] = useState<string | null>(null);
  const [keyResultDraft, setKeyResultDraft] = useState<string | null>(null);
  // Which row is open, at most one (ADR A3b). The card's state machine stays
  // flat: at most one of titleDraft / keyResultDraft / editingResultUuid is
  // non-null. `resultDraft` is only meaningful while editingResultUuid is set.
  const [editingResultUuid, setEditingResultUuid] = useState<string | null>(null);
  const [resultDraft, setResultDraft] = useState('');
  const [copied, setCopied] = useState(false);
  // The details dialog (ADR A4b) is NOT part of the isEditing state machine
  // above: it is modal and portaled out of the canvas (Headless.Dialog), so
  // nothing in the card competes for the pointer while it is open, and it
  // costs zero card height in any state (A1e).
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoDraft, setInfoDraft] = useState('');

  /**
   * The draft reset on entering `titleOnly` (ADR B4a). An effect rather than
   * a reset inside the card's own click handler, because a handler covers
   * only ONE of the two ways a card becomes collapsed — the column's
   * collapse-all (B6, a future task) mutates the collapse set without the
   * card's own buttons being touched at all, and a handler-side reset would
   * silently miss that path. Runs after commit, which is harmless: the
   * collapsed render contains no editor either way, so this is about what is
   * restored on re-expand, not about what paints while collapsed.
   */
  useEffect(() => {
    if (card.collapse !== 'titleOnly') return;
    setTitleDraft(null);
    setKeyResultDraft(null);
    setEditingResultUuid(null);
  }, [card.collapse]);

  // The one accessible/visible name for this objective, shared by both renders.
  const objectiveLabel = card.text || 'Untitled objective';

  /* -----------------------------------------------------------------------
   * Status colour (ADR.ImplementationPlansUX2 C1b/C1d/C1f). The card carries
   * the four PRIMITIVES; the class and the label are derived here, in the
   * presenter, exactly as `StatusCellPresenter` derives the grid cell's. Both
   * come from one total function, so the label can never name a colour the
   * card is not wearing, and `null` is a single, coherent state: no rail, no
   * hairline, no tint, and the neutral gray default.
   * --------------------------------------------------------------------- */
  const statusClass = cardStatusColorClass(
    card.statusCode,
    card.hasResults,
    card.hasKeyResults,
    card.objectiveIsParent,
  );
  const statusLabel = cardStatusLabel(
    card.statusCode,
    card.hasResults,
    card.hasKeyResults,
    card.objectiveIsParent,
  );

  // The card root is identical in both collapse states — same classes, same
  // inline box — so the height budget, the A1-R1a drag ring and INV-16's
  // "the root never clips" all hold across the transition by construction
  // rather than by two literals staying in step.
  //
  // With a status the root becomes an opaque WHITE substrate (`bg-white`) that
  // the 45% tint composites over — the composite C1d-R1's contrast arithmetic
  // is computed against — plus `relative isolate`, which is what confines the
  // two `-z-10` layers to this card. Without one it is the NEUTRAL GRAY
  // default (C1d, mr-60dv.14), replacing the shipped amber: yellow on this
  // board now comes only from a yellow status. `zinc-100`/`zinc-200` are fixed
  // Tailwind tokens, deliberately not `bg-status-p` — a status token's value
  // moves with the theme (grayscale maps `--status-p` to `#ffffff`), and a
  // full-strength status fill under card text is exactly what C1d forbids.
  const cardRootClassName = statusClass
    ? 'relative isolate flex flex-col rounded-md border border-zinc-950/10 bg-white p-3 shadow-sm'
    : 'flex flex-col rounded-md border border-zinc-200 bg-zinc-100 p-3 shadow-sm';
  const cardRootStyle: CSSProperties = { width: data.width, height: data.height };
  // The pointer-only twin of the node wrapper's accessible name (C1f). Since
  // mr-60dv.13 removed the visible glyph these two strings are the card's ONLY
  // textual carriers of status — matching the grid, which is colour-only until
  // `showStatusCodes` is switched on. Both carry the same string so the name a
  // screen reader announces and the tooltip a mouse user sees agree.
  const cardRootTitle = `Objective: ${objectiveLabel}. Status: ${statusLabel}.`;

  /**
   * The rail and the tint — one definition each, rendered in BOTH collapse
   * states so a collapsed card carries its status exactly as an expanded one
   * does.
   *
   * `null` when the objective has no status or is a parent on this map: the
   * card then wears the neutral gray default, and that absence — no rail, no
   * hairline — is itself the cue that separates it from a card whose latest
   * status is `'p'`, which wears the same family of gray *with* a rail.
   */
  const statusLayers = statusClass ? (
    <>
      {/* The TINT — gestalt, not fidelity, at exactly 45%. An inline style,
          not an `opacity-*` class: 45 is not on Tailwind's scale, and the
          figure is load-bearing arithmetic (C1d-R1a) rather than a taste
          setting. `-inset-px` covers the root's 1px border, which `inset-0`
          would leave as an uncoloured ring showing through it. */}
      <div
        aria-hidden="true"
        data-status-layer="tint"
        className={`pointer-events-none absolute -inset-px -z-10 rounded-md ${statusClass}`}
        style={{ opacity: 0.45 }}
      />
      {/* The RAIL — fidelity, at 100% opacity, painted AFTER the tint so it
          sits over it. `w-2` (8px) is strictly inside the card's own `p-3`
          (12px) left padding, so it overlaps no content and steals no width;
          `border-r border-zinc-900` is inside that 8px under `border-box`, and
          is the only thing that makes a `#ffffff` grayscale token's rail
          visible at all (C1d-R2, INV-19). */}
      <div
        aria-hidden="true"
        data-status-layer="rail"
        className={`pointer-events-none absolute inset-y-0 left-0 -z-10 w-2 rounded-l-md border-r border-zinc-900 ${statusClass}`}
      />
    </>
  ) : null;

  /* -----------------------------------------------------------------------
   * The title-only render (ADR B5): ONE block, one truncated title, nothing
   * else. Placed after every hook so the hook order is identical in both
   * renders — the component instance survives a collapse (same node id), and
   * only its returned tree changes.
   * --------------------------------------------------------------------- */
  if (card.collapse === 'titleOnly') {
    return (
      <div
        data-block="card"
        className={cardRootClassName}
        style={cardRootStyle}
        title={cardRootTitle}
      >
        {/* The two colour layers are the root's first two children in BOTH
            renders, tint first so the rail paints over it (C1d). */}
        {statusLayers}
        {/* Pinned, unshrinkable, and NON-clipping: it wraps the card's view
            controls, so it budgets 2 × FOCUS_RING_CLEARANCE rather than
            clipping their focus rings, exactly as the eyebrow does
            (A1-R1b, INV-16, WCAG 2.4.7). `gap-2` is the row's budgeted
            separation between the title and the controls at its right end. */}
        <div
          data-block="collapsed"
          className="flex items-center gap-2"
          style={{ height: COLLAPSED_BLOCK_HEIGHT, flexShrink: 0 }}
        >
          {/* The clip travels with the text (B3-2): `truncate` and the `title`
              disclosure live on the element that OWNS the text, never on the
              row — `truncate` computes to `overflow: hidden`, which on the row
              would erase its children's focus rings instead of trimming them.
              `min-w-0` is load-bearing and not decoration: a flex item's
              `min-width` defaults to `auto`, i.e. its content's minimum, so
              without it the title refuses to shrink, `text-overflow: ellipsis`
              never engages (there is no overflow to ellipsise), and the row's
              controls are pushed out of a card whose width is fixed.
              `MapTitleBar.tsx:96` carries the identical pin on the identical
              construction. The ellipsis is the browser's own — no measurement,
              no JS truncation (INV-6).
              A plain non-interactive <div>, deliberately (B5): it carries no
              `nodrag`, so the whole row is drag surface (B8), and its `title`
              tooltip is pointer-only — the keyboard route to a truncated
              collapsed title is to expand the card (INV-16's amended row).
              No status glyph here or in the eyebrow (mr-60dv.13): the card's
              status reads through colour plus the node wrapper's accessible
              name, which is the grid's own default posture. */}
          <div
            className="min-w-0 flex-1 truncate text-sm/5 font-medium text-zinc-900"
            title={objectiveLabel}
          >
            {objectiveLabel}
          </div>
          {/* The card-view pair (ADR B4/B4a): ALWAYS rendered here, never
              gated on isEditing — computed below this early return — which
              is precisely what makes a collapsed card escapable whatever
              state the component instance is in (the round-1 Critical this
              guards against). */}
          <CardViewControls card={card} objectiveLabel={objectiveLabel} onSetCollapse={onSetCollapse} />
        </div>
      </div>
    );
  }

  // Copy-only — no navigation, no query params (ADR A2b): Map3Drawer lives
  // inside Map3, which Map3Workspace hides whenever a named view is active,
  // so a drawer opened from this board would mount hidden. Verbatim pattern
  // from Map3Drawer.tsx's handleBadgeCopy (ADR A2a).
  const handleBadgeCopy = async () => {
    if (!card.shortId) return;
    try {
      await navigator.clipboard.writeText(card.shortId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // silently fail
    }
  };

  const submitTitle = () => {
    if (titleDraft === null) return;
    const trimmed = titleDraft.trim();
    if (trimmed && trimmed !== card.text) {
      onEditTitle?.(card.uuid, trimmed);
    }
    setTitleDraft(null);
  };

  const submitKeyResult = () => {
    if (keyResultDraft === null) return;
    const trimmed = keyResultDraft.trim();
    if (trimmed) {
      onAddKeyResult?.(card.uuid, trimmed);
    }
    setKeyResultDraft(null);
  };

  /**
   * Commits a row edit (ADR A3b) — the title's contract, verbatim: Enter or
   * blur submits a non-empty trimmed value, Escape cancels, and an empty or
   * unchanged value dispatches nothing (INV-9). Only `text` travels; the
   * wrapper preserves everything else on the result (A3a, A3d).
   */
  const submitKeyResultEdit = () => {
    if (editingResultUuid === null) return;
    const trimmed = resultDraft.trim();
    const original = card.keyResults.find((kr) => kr.uuid === editingResultUuid);
    if (trimmed && trimmed !== original?.text) {
      onEditKeyResult?.(card.uuid, editingResultUuid, trimmed);
    }
    setEditingResultUuid(null);
  };

  const cancelKeyResultEdit = () => setEditingResultUuid(null);

  /**
   * The details dialog (ADR A4b/A4c). `openDetailsDialog` seeds the draft from
   * the card's CURRENT value every time it opens — never from a stale draft
   * left over from a previous open/cancel. `submitDetails` passes the raw
   * draft straight through: trimming and the no-op/`canEdit` guards are the
   * wrapper's job (A4c), read store-fresh at commit time, exactly like every
   * other handler here. There is no viewer path to `submitDetails` at all —
   * the viewer's dialog renders a single Close button, never Save (A4b-i).
   */
  const openDetailsDialog = () => {
    setInfoDraft(card.additionalInfo);
    setInfoOpen(true);
  };
  const closeDetailsDialog = () => setInfoOpen(false);
  const submitDetails = () => {
    onEditAdditionalInfo?.(card.uuid, infoDraft);
    setInfoOpen(false);
  };

  // Any open inline editor hides the move controls (A3b): a footer control is a
  // live pointer target during an edit, so one click would blur-commit the edit
  // AND dispatch a move — two writes to the same objective from one gesture,
  // ordered by event timing. A move also relocates the card's DOM node, which
  // resets document.activeElement and blur-commits the edit as a side effect,
  // with no INV-11 restore record for an in-flight input.
  const isEditing = titleDraft !== null || keyResultDraft !== null || editingResultUuid !== null;

  const keyResultCount = card.keyResults.length;
  // The one expression, shared with computeLayout's cardHeight call so the
  // budgeted block and the rendered block can never disagree (ADR A1d).
  const showInfo = cardHasAdditionalInfo(card);

  // The details trigger's three-state accessible name (ADR A4b-i table) —
  // an icon-only button, so aria-label IS the name. The dialog's own title
  // carries the identical string, so the label a user activates and the
  // heading they land on agree.
  const detailsAriaLabel = !canEdit
    ? `View details: ${objectiveLabel}`
    : showInfo
      ? `Edit details: ${objectiveLabel}`
      : `Add details: ${objectiveLabel}`;
  const detailsButtonTitle = !canEdit ? 'View details' : showInfo ? 'Edit details' : 'Add details';

  // The title block IS the control in all three of its renders (A1-R1b), so the
  // pin travels with it — including into the edit state (A1e / INV-13).
  const titleBlockStyle: CSSProperties = {
    height: TITLE_BLOCK_HEIGHT,
    flexShrink: 0,
    overflow: 'hidden',
  };
  // Every footer child is unshrinkable: the row is budgeted to zero slack
  // (A5a/D1), so a shrinkable child would absorb an over-budget row silently.
  const footerChildStyle: CSSProperties = { flexShrink: 0 };

  return (
    <div
      data-block="card"
      className={cardRootClassName}
      style={cardRootStyle}
      title={cardRootTitle}
    >
      {/* The two colour layers are the root's first two children in BOTH
          renders, tint first so the rail paints over it (C1d). */}
      {statusLayers}
      {/* Eyebrow: pinned, never clipping, and NOT conditional — an objective
          with no shortId renders the band empty of the badge (still holding
          the B4 pair below) at the same height, so card height never
          flickers on bad data (A1b, A1d). The badge is visible to every role
          (copying an id is not an edit, A2a note 4); it copies only, it
          never navigates (A2b). */}
      <div
        data-block="eyebrow"
        className="flex items-center gap-2"
        style={{ height: EYEBROW_BLOCK_HEIGHT, flexShrink: 0 }}
      >
        {card.shortId && (
          <>
            {/* The opaque plate (C1d-R1b). Catalyst puts `className` on the
                outer button and renders the translucent `<Badge>` INSIDE it,
                so no class can reach the element the `bg-zinc-600/10` fill is
                on: over the tint it composites to 4.44:1 (3.96:1 on hover).
                Wrapping it in `bg-white` restores Catalyst's designed 8.98:1
                without editing `components_catalyst/`. */}
            <span data-badge-plate="" className="inline-flex rounded-md bg-white">
              <BadgeButton
                color="zinc"
                className="nodrag"
                onClick={handleBadgeCopy}
                aria-label={`Copy objective short ID: ${card.shortId}`}
                title="Click to copy objective short ID"
              >
                {card.shortId}
              </BadgeButton>
            </span>
            <span
              aria-live="polite"
              className={`text-xs text-zinc-900 transition-opacity ${copied ? 'opacity-100' : 'opacity-0'}`}
            >
              Copied!
            </span>
          </>
        )}
        {/* The card-view pair (ADR B4/B4a): top-right of the eyebrow
            (`ml-auto`), gated on isEditing alongside the footer's move
            controls and details trigger — the EXPANDED-render half of B4a's
            gate. Renders for every role: collapsing is a view operation, not
            an edit (A2a's reasoning, applied here). */}
        {!isEditing && (
          <CardViewControls
            card={card}
            objectiveLabel={objectiveLabel}
            onSetCollapse={onSetCollapse}
            className="ml-auto"
          />
        )}
      </div>

      {/* Title: click to edit (nodrag on both the control and the input). */}
      {titleDraft !== null ? (
        <input
          data-block="title"
          type="text"
          className="nodrag w-full rounded border border-zinc-500 bg-white px-1 text-sm/5 font-medium text-zinc-900"
          aria-label="Objective title"
          style={titleBlockStyle}
          value={titleDraft}
          autoFocus
          onChange={(e) => setTitleDraft(e.target.value)}
          onBlur={submitTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submitTitle();
            if (e.key === 'Escape') setTitleDraft(null);
          }}
        />
      ) : canEdit ? (
        <button
          data-block="title"
          type="button"
          className="nodrag line-clamp-2 text-left text-sm/5 font-medium text-zinc-900"
          aria-label={`Edit objective title: ${card.text || 'Untitled objective'}`}
          style={titleBlockStyle}
          onClick={() => setTitleDraft(card.text)}
        >
          {card.text || 'Untitled objective'}
        </button>
      ) : (
        <div
          data-block="title"
          className="line-clamp-2 text-sm/5 font-medium text-zinc-900"
          style={titleBlockStyle}
        >
          {card.text || 'Untitled objective'}
        </div>
      )}

      {/* Additional info: conditional (A1b) and self-clipping — the block IS
          the text-owning element (A1-R1b), so the clamp class and the `title`
          disclosure both live here, matching the key-result row pattern. Its
          editor lives in the footer dialog (A4b), never inline (A1e).
          `whitespace-pre-wrap break-words` (ADR B7) makes the preview honour
          the same line breaks and indentation the edit dialog's `Textarea`
          already renders by default, so the two agree by construction; the
          clamp, the height and the inline pins are untouched. */}
      {showInfo && (
        <div
          data-block="info"
          className="line-clamp-2 whitespace-pre-wrap break-words text-xs/4 text-zinc-900"
          style={{
            height: INFO_BLOCK_HEIGHT,
            paddingTop: INFO_BLOCK_GAP,
            flexShrink: 0,
            overflow: 'hidden',
          }}
          title={card.additionalInfo}
        >
          {card.additionalInfo}
        </div>
      )}

      {/* Key results: the <ul> is pinned as ONE block so the leading gap cannot
          be double-counted. Its marginTop is the card's only margin, and it is
          inline-pinned (was `mt-2` = 8, which is L1's 4px residue). */}
      {keyResultCount > 0 && (
        <ul
          data-block="key-results"
          className="space-y-1"
          aria-label="Key results"
          style={{
            marginTop: KEY_RESULT_LIST_MARGIN_TOP,
            height: keyResultsListHeight(keyResultCount),
            flexShrink: 0,
          }}
        >
          {card.keyResults.map((kr) => (
            // The row hosts TWO controls now — the checkbox and the text
            // control — so the <li> becomes a flex row (ADR.ImplementationPlansUX2
            // C2a): `items-center` against the pinned height is what centres
            // the intrinsically-sized 18px checkbox, and `gap-1.5` is the
            // row's only new horizontal cost. It is still a pinned wrapper
            // that must NOT clip (A1-R1b/B3-2): with zero clearance in a 20px
            // box an ancestor clip erases a control's focus ring rather than
            // trimming it. `truncate` and `title` travel with the text onto
            // the control below — they are one affordance (clip + hover
            // disclosure), so splitting them would put the tooltip on a box
            // whose text no longer overflows. `flexShrink: 0` stays pinned
            // (finding B-2) — making the <li> a flex CONTAINER does not make
            // it a flex ITEM of its own parent <ul>.
            <li
              key={kr.uuid}
              className="flex items-center gap-1.5"
              style={{ height: KEY_RESULT_ROW_BOX_HEIGHT, flexShrink: 0 }}
            >
              {/* The checkbox (ADR C2a/C2b): Catalyst's `Checkbox`, first
                  child so it sits left of the text in all three renders — the
                  row's control count and geometry must not depend on role or
                  edit state (A1d). Its `className` reaches the interactive
                  element (fact 10), so `nodrag` lands on the pointer target;
                  `data-control` is UNIQUE per row, unlike every other control
                  on this card, because restoreFocus takes the first
                  `[data-control]` match in the card and a shared name would
                  focus row 1 after a row-4 toggle (C6b). `checked`/`onChange`
                  only — Headless UI supplies `role="checkbox"`/`aria-checked`.
                  `!canEdit` renders it `disabled` with `aria-checked` still
                  intact (C2c) — a viewer can read delivered state, just not
                  tab to or flip it.
                  `cursor-default` is hover PARITY with the app's other result
                  checkboxes (C2a, bead mr-60dv.12). `ResultItem3`'s native
                  `<input type="checkbox">` sets no cursor and so shows the
                  platform arrow; this control is a Headless UI `<span>` inside
                  a ReactFlow node, and `cursor` INHERITS — so with no class of
                  its own it picks up `.react-flow__node.draggable`'s `grab`
                  (editor) or `.selectable`'s `pointer` (viewer) from the
                  library stylesheet, both advertising an affordance the
                  checkbox does not have. One class covers both roles and the
                  disabled case: Tailwind preflight's `:disabled { cursor:
                  default }` cannot reach a Headless UI span (it is
                  `aria-disabled`, not a disabled input), so this class is also
                  what keeps a viewer's checkbox matching. */}
              <Checkbox
                className="nodrag cursor-default"
                data-control={`toggle-delivered:${kr.uuid}`}
                checked={kr.delivered}
                disabled={!canEdit}
                aria-label={
                  kr.delivered
                    ? `Mark key result incomplete: ${kr.text}`
                    : `Mark key result complete: ${kr.text}`
                }
                onChange={(delivered) => onToggleDelivered?.(card.uuid, kr.uuid, delivered)}
              />
              {editingResultUuid === kr.uuid ? (
                // A 20px row constrains this input: text-xs (12/16) + a 1px
                // border top and bottom = 18, so px-1 and no py-* fits —
                // copying the title input's classes verbatim does not.
                // `min-w-0 flex-1` alongside the shipped `w-full` (C2a): a
                // flex item's `min-width: auto` refuses to shrink without it,
                // which would push the checkbox out of the fixed-width card.
                <input
                  type="text"
                  className="nodrag min-w-0 flex-1 w-full truncate rounded border border-zinc-500 bg-white px-1 text-xs text-zinc-900"
                  aria-label="Key result"
                  value={resultDraft}
                  autoFocus
                  onChange={(e) => setResultDraft(e.target.value)}
                  onBlur={submitKeyResultEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitKeyResultEdit();
                    if (e.key === 'Escape') cancelKeyResultEdit();
                  }}
                />
              ) : canEdit ? (
                <button
                  type="button"
                  className="nodrag min-w-0 flex-1 truncate text-left text-xs text-zinc-900"
                  aria-label={`Edit key result: ${kr.text}`}
                  title={kr.text}
                  onClick={() => {
                    setEditingResultUuid(kr.uuid);
                    setResultDraft(kr.text);
                  }}
                >
                  {kr.text}
                </button>
              ) : (
                // A read-only viewer gets text, never a button that does
                // nothing (A3b) — the pinned row box is identical in all three
                // renders.
                <div className="min-w-0 flex-1 truncate text-xs text-zinc-900" title={kr.text}>
                  {kr.text}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Footer: pinned and reserved for EVERY role (A1d) — its height is part
          of the budget whether or not the viewer can use its controls, so two
          clients with different roles never disagree about card geometry. No
          `mt-auto`: with an exact budget there is nothing for it to absorb, and
          a mechanism whose job is to make a budget error look fine is the same
          species as the shrink that made B-2 invisible. */}
      <div
        data-block="footer"
        className="flex items-center justify-center gap-1"
        style={{ height: FOOTER_HEIGHT, flexShrink: 0 }}
      >
        {canEdit && (
          <>
            {/* Move controls hide while ANY inline edit is open (A3b): the
                add-key-result input would otherwise overflow the fixed card
                width, and for the title and row editors a footer click would
                blur-commit the edit and dispatch a move from the same
                gesture. */}
            {!isEditing && (
              <>
                <button
                  type="button"
                  data-control="move-up"
                  className="nodrag rounded p-0.5 text-zinc-900 hover:bg-black/5"
                  aria-label={`Move ${card.text || 'objective'} up`}
                  style={footerChildStyle}
                  onClick={() => onKeyboardMove?.(card.uuid, { type: 'up' })}
                >
                  <ChevronUpIcon className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  data-control="move-down"
                  className="nodrag rounded p-0.5 text-zinc-900 hover:bg-black/5"
                  aria-label={`Move ${card.text || 'objective'} down`}
                  style={footerChildStyle}
                  onClick={() => onKeyboardMove?.(card.uuid, { type: 'down' })}
                >
                  <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
                </button>
                {/* Catalyst `Select` wraps a NATIVE <select> with no portal, so
                    the keyboard, screen-reader and INV-10 Windows behaviours are
                    preserved verbatim (finding D1). Two things it cannot give us
                    from a className: `className` lands on Catalyst's wrapper
                    <span>, where `w-full` beats `w-36` on Tailwind's emission
                    order (M-1) — hence this sizing <div> with an inline width —
                    and the control's own padding/font-size are responsive
                    (B2/M-4), so both axes are pinned inline on the <select>,
                    which the cascade cannot touch. `data-sizer` rather than
                    `data-block` (it would join the block-pin loop with no height
                    pin) or `data-control` (focusRestore takes the first match in
                    tree order and would focus a non-focusable div). */}
                <div
                  data-sizer="move-column"
                  style={{ width: FOOTER_SELECT_WIDTH, flexShrink: 0 }}
                >
                  <Select
                    className="nodrag"
                    style={{
                      height: FOOTER_HEIGHT,
                      paddingTop: FOOTER_SELECT_PAD_Y,
                      paddingBottom: FOOTER_SELECT_PAD_Y,
                      paddingLeft: FOOTER_SELECT_PAD_X_START,
                      paddingRight: FOOTER_SELECT_PAD_X_END,
                      fontSize: FOOTER_SELECT_FONT_SIZE,
                    }}
                    data-control="move-column"
                    aria-label={`Move ${card.text || 'objective'} to column`}
                    value={card.column}
                    onChange={(e) =>
                      onKeyboardMove?.(card.uuid, {
                        type: 'column',
                        column: e.target.value as ImplementationPlanColumn,
                      })
                    }
                  >
                    {PLAN_COLUMNS.map((col) => (
                      <option key={col.id} value={col.id}>
                        {col.label}
                      </option>
                    ))}
                  </Select>
                </div>
              </>
            )}
            {keyResultDraft !== null ? (
              <input
                type="text"
                className="nodrag w-full rounded border border-zinc-500 bg-white px-1 text-xs text-zinc-900"
                aria-label="New key result"
                placeholder="Key result…"
                style={footerChildStyle}
                value={keyResultDraft}
                autoFocus
                onChange={(e) => setKeyResultDraft(e.target.value)}
                onBlur={submitKeyResult}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitKeyResult();
                  if (e.key === 'Escape') setKeyResultDraft(null);
                }}
              />
            ) : (
              <button
                type="button"
                className="nodrag rounded p-0.5 text-zinc-900 hover:bg-black/5"
                aria-label="Add key result"
                style={footerChildStyle}
                onClick={() => setKeyResultDraft('')}
              >
                <PlusIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </>
        )}
        {/* Details trigger (ADR A4b/A4b-i): OUTSIDE the canEdit fragment above
            — it renders for EVERY role, not just editors, which is what makes
            additionalInfo's full text keyboard-reachable for a viewer. Hidden
            only while another inline edit is open (A3b); the dialog itself
            needs no such gate — it is modal and portaled, so nothing in the
            card competes with it for the pointer. */}
        {!isEditing && (
          <button
            type="button"
            className="nodrag rounded p-0.5 text-zinc-900 hover:bg-black/5"
            aria-label={detailsAriaLabel}
            title={detailsButtonTitle}
            style={footerChildStyle}
            onClick={openDetailsDialog}
          >
            <DocumentTextIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* The details dialog (ADR A4b). Portaled by Headless.Dialog, so it is
          rendered here for readability but costs nothing to the card's own
          box regardless of open state (A1e) and needs no `nodrag` — nothing
          inside it is a descendant of the ReactFlow node. Editors get a
          Save/Cancel editor; viewers get a readOnly Textarea and a single
          Close button, never Save (A4b-i) — canEdit is an affordance here,
          not the authorization boundary, but there is deliberately no path
          from the viewer's markup to submitDetails at all. */}
      <Dialog open={infoOpen} onClose={closeDetailsDialog}>
        <DialogTitle>{detailsAriaLabel}</DialogTitle>
        <DialogBody>
          <Textarea
            aria-label="Additional info"
            rows={8}
            value={infoDraft}
            readOnly={!canEdit}
            onChange={(e) => setInfoDraft(e.target.value)}
          />
        </DialogBody>
        <DialogActions>
          {canEdit ? (
            <>
              <Button plain onClick={closeDetailsDialog}>
                Cancel
              </Button>
              <Button onClick={submitDetails}>Save</Button>
            </>
          ) : (
            <Button onClick={closeDetailsDialog}>Close</Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}
